import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';

// ============================================================
// JWT Payload Interface
// ============================================================

interface JwtPayload {
    nameid?: string;
    sub?: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string | string[];
    permission?: string | string[];
    exp?: number;
    iat?: number;
    nbf?: number;
    iss?: string;
    aud?: string;
    [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

    // ============================================================
    // Private Fields
    // ============================================================

    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    // ============================================================
    // API URL
    // ============================================================

    private get apiUrl(): string {
        const base = localStorage.getItem('apiURL');
        if (!base) throw new Error('apiURL not ready');
        return base + '/api/v2';
    }

    // ============================================================
    // Constructor
    // ============================================================

    constructor() {
        // لا حاجة لأي شيء هنا
    }

    // ============================================================
    // Access Token
    // ============================================================

    /**
     * Setter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    /**
     * Getter for access token
     */
    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    // ============================================================
    // JWT Decoding
    // ============================================================

    /**
     * Decode JWT payload — آمن مع Unicode
     */
    private decodeToken(token?: string): JwtPayload | null {
        const jwt = token ?? this.accessToken;
        if (!jwt) return null;

        try {
            const parts = jwt.split('.');
            if (parts.length !== 3) return null;

            // Base64Url → Base64
            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

            // Padding
            const padded = base64.padEnd(
                base64.length + (4 - (base64.length % 4)) % 4,
                '='
            );

            // Decode with Unicode support
            const decoded = decodeURIComponent(
                atob(padded)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );

            return JSON.parse(decoded) as JwtPayload;
        } catch (error) {
            console.error('❌ Failed to decode JWT:', error);
            return null;
        }
    }

    /**
     * Get full JWT payload
     */
    getPayload(): JwtPayload | null {
        return this.decodeToken();
    }

    // ============================================================
    // User Info
    // ============================================================

    get userId(): string {
        const p = this.getPayload();
        return p?.nameid ?? p?.sub ?? '';
    }

    get userName(): string {
        return this.getPayload()?.name ?? '';
    }

    get userEmail(): string {
        return this.getPayload()?.email ?? '';
    }

    get userPhone(): string {
        return this.getPayload()?.phone ?? '';
    }

    // ============================================================
    // Roles
    // ============================================================

    /**
     * كل الـ Roles الموجودة في التوكن
     */
    getRoles(): string[] {
        const p = this.getPayload();
        if (!p) return [];

        const role = p.role;

        if (Array.isArray(role)) return role;
        if (typeof role === 'string') return [role];

        return [];
    }

    /**
     * هل اليوزر عنده Role معين؟
     */
    hasRole(role: string): boolean {
        return this.getRoles().includes(role);
    }

    /**
     * هل اليوزر عنده أي واحد من الـ Roles دي؟
     */
    hasAnyRole(...roles: string[]): boolean {
        const userRoles = this.getRoles();
        return roles.some(r => userRoles.includes(r));
    }

    // ============================================================
    // Permissions
    // ============================================================

    /**
     * كل الـ Permissions الموجودة في التوكن
     */
    getPermissions(): string[] {
        const p = this.getPayload();
        if (!p) return [];

        const permission = p.permission;

        if (Array.isArray(permission)) return permission;
        if (typeof permission === 'string') return [permission];

        return [];
    }

    /**
     * هل اليوزر عنده Permission معين؟
     */
    hasPermission(permission: string): boolean {
        return this.getPermissions().includes(permission);
    }

    /**
     * هل اليوزر عنده أي واحد من الـ Permissions دي؟
     */
    hasAnyPermission(...permissions: string[]): boolean {
        const userPerms = this.getPermissions();
        return permissions.some(p => userPerms.includes(p));
    }

    /**
     * هل اليوزر عنده كل الـ Permissions دي؟
     */
    hasAllPermissions(...permissions: string[]): boolean {
        const userPerms = this.getPermissions();
        return permissions.every(p => userPerms.includes(p));
    }

    // ============================================================
    // Token Validity
    // ============================================================

    /**
     * هل التوكن لسه صالح؟
     */
    isTokenValid(): boolean {
        const p = this.getPayload();
        if (!p || !p.exp) return false;

        const now = Math.floor(Date.now() / 1000);
        return p.exp > now;
    }

    /**
     * وقت انتهاء التوكن
     */
    getExpirationDate(): Date | null {
        const p = this.getPayload();
        if (!p?.exp) return null;
        return new Date(p.exp * 1000);
    }

    /**
     * هل اليوزر مسجل دخول؟
     */
    isAuthenticated(): boolean {
        return !!this.accessToken && this.isTokenValid();
    }

    // ============================================================
    // Public Auth Methods
    // ============================================================

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post(this.apiUrl + '/auth/login', credentials).pipe(
            switchMap((response: any) => {
                console.log('🔐 Login Response:', response);

                // Store the access token
                this.accessToken = response.access_token;

                // Set the authenticated flag
                this._authenticated = true;

                // Store the user
                this._userService.user = response.user;

                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        return this._httpClient.post(this.apiUrl + '/auth/sign-in-with-token', {
            Access_Token: this.accessToken,
        }).pipe(
            catchError(() => of(false)),
            switchMap((response: any) => {
                console.log('🔑 SignInUsingToken Response:', response);

                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                }

                this._authenticated = true;
                this._userService.user = response.user;

                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        localStorage.removeItem('accessToken');
        this._authenticated = false;
        return of(true);
    }

    /**
     * Sign up
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    // ============================================================
    // Debug Helper
    // ============================================================

    /**
     * اطبع كل حاجة في التوكن للـ Console
     */
    private inspectToken(token: string): void {
        try {
            const parts = token.split('.');
            if (parts.length === 3) {
                const payload = this.decodeToken(token);
                console.log('🔍 ========== TOKEN INSPECT ==========');
                console.log('📦 Full Payload:', payload);
                console.log('🎭 Roles:', this.getRoles());
                console.log('🔐 Permissions:', this.getPermissions());
                console.log('⏰ Expires At:', this.getExpirationDate());
                console.log('✅ Valid:', this.isTokenValid());
                console.log('====================================');
            }
        } catch (error) {
            console.error('🔍 - Error parsing token:', error);
        }
    }

    // ============================================================
    // Check Authentication
    // ============================================================

    check(): Observable<boolean> {
        // Log token details
        if (this.accessToken) {
            this.inspectToken(this.accessToken);
        }

        // Already authenticated
        if (this._authenticated) {
            return of(true);
        }

        // No token
        if (!this.accessToken) {
            console.log('❌ Check: No access token');
            this._authenticated = false;
            return of(false);
        }

        // Token expired
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            console.log('❌ Check: Token expired');
            this._authenticated = false;
            localStorage.removeItem('accessToken');
            return of(false);
        }

        console.log('🔄 Check: Attempting to authenticate with token');

        // Try to authenticate
        return this.signInUsingToken().pipe(
            switchMap((result) => {
                console.log('🔑 SignInUsingToken result:', result);
                this._authenticated = result;
                return of(result);
            }),
            catchError((error) => {
                console.error('❌ SignInUsingToken error:', error);
                this._authenticated = false;
                return of(false);
            })
        );
    }
}